import nav from '../_nav';

/**
 * Rebuild admin sidebar menu from current _nav order/structure.
 * Custom names from savedMenu apply only when id or url matches.
 * Legacy orphan rows (stale indexes, obsolete urls) are dropped.
 *
 * This keeps Company Settings Menu and channel-admin nav in the same sequence.
 */
function collectSavedByKey(savedItems, map = new Map()) {
  (savedItems || []).forEach((item) => {
    if (!item || typeof item !== 'object') {
      return;
    }
    if (item.id != null && item.id !== '') {
      const idKey = `id:${item.id}`;
      if (!map.has(idKey)) {
        map.set(idKey, item);
      }
    }
    if (item.url) {
      const urlKey = `url:${item.url}`;
      if (!map.has(urlKey)) {
        map.set(urlKey, item);
      }
    }
    if (Array.isArray(item.children) && item.children.length) {
      collectSavedByKey(item.children, map);
    }
  });
  return map;
}

function cloneNavItem(item) {
  const clone = { ...item };
  if (Array.isArray(item.children)) {
    clone.children = item.children.map((child) => cloneNavItem(child));
  }
  return clone;
}

function applySavedNames(def, savedMap) {
  const byId = def.id != null && def.id !== '' ? savedMap.get(`id:${def.id}`) : null;
  const byUrl = def.url ? savedMap.get(`url:${def.url}`) : null;
  const saved = byId || byUrl;
  const next = cloneNavItem(def);

  if (saved && saved.name) {
    next.name = saved.name;
  }

  if (Array.isArray(def.children) && def.children.length) {
    next.children = def.children.map((child) => applySavedNames(child, savedMap));
  }

  return next;
}

/**
 * Re-apply org-custom names onto an already-built nav list (e.g. after normalizers
 * re-inject default items). Does not add/remove items.
 */
export function applySavedAdminMenuNames(items, savedMenu) {
  const savedMap = collectSavedByKey(savedMenu || []);
  (items || []).forEach((item) => {
    if (!item || typeof item !== 'object') {
      return;
    }
    const byId = item.id != null && item.id !== '' ? savedMap.get(`id:${item.id}`) : null;
    const byUrl = item.url ? savedMap.get(`url:${item.url}`) : null;
    const saved = byId || byUrl;
    if (saved && saved.name) {
      item.name = saved.name;
    }
    if (Array.isArray(item.children) && item.children.length) {
      applySavedAdminMenuNames(item.children, savedMenu);
    }
  });
  return items;
}

export function canonicalizeAdminMenu(savedMenu, { editableOnly = false, userGroup = null } = {}) {
  let defaults = nav.items.map((item) => cloneNavItem(item));

  if (editableOnly) {
    defaults = defaults.filter(
      (menu) => menu.separator === undefined && menu.title === undefined && menu.spacer === undefined
    );
  }

  if (userGroup != null && userGroup !== '') {
    const group = Number(userGroup);
    defaults = defaults.filter((menu) => {
      const groups = (menu.userGroups || []).map((g) => Number(g));
      return groups.includes(group);
    });
  }

  const savedMap = collectSavedByKey(savedMenu || []);
  return defaults
    .map((def) => applySavedNames(def, savedMap))
    .sort((a, b) => (a.index ?? 0) - (b.index ?? 0));
}
